// Phase 4.7 (hh-isk) — ProfileScreen.
//
// Ported from designproject/files/app.jsx (`ProfileScreen`). Открывается из
// bottom-nav (вкладка profile, точка входа — аватар в шапке FeedScreen).
// Статистика (объекты / вещи / оборудование) считается по реальному дереву
// GET /api/v1/properties/tree. Модели пользователя нет (бриф — один человек),
// поэтому имя не персистится — карточка показывает обобщённый профиль.

import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { T, Icon, Avatar, Btn, SectionLabel, type IconName } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import type { ScreenId, TabId } from '../../router';
import { properties, humanizeError, auth } from '../../api';
import { enablePushNotifications, isPushSupported } from '../push';
import type { PushSetupResult } from '../push';
import {
  getVoiceEnginePref,
  setVoiceEnginePref,
  isNativeSttSupported,
} from '../voicedock/voiceEngine';
import type { VoiceEnginePref } from '../voicedock/voiceEngine';

export interface ProfileScreenProps {
  readonly router: RouterApi;
}

interface ScenarioRow {
  readonly icon: IconName;
  readonly label: string;
  readonly go?: TabId | ScreenId;
  /**
   * Временный гейт точки входа. Строка не рендерится, пока true.
   * Код самой фичи (экран + роут в AppShell) остаётся на месте — чтобы вернуть
   * сценарий, достаточно убрать флаг здесь.
   *  - 'Проход по дому' (walkthrough) — aidepo-45: кладёт оборудование в случайную
   *    property (котёл попал в Toyota Camry). Вход скрыт, пока логика размещения
   *    не починена.
   *  - 'Обучающие ролики' (videos) — aidepo-51: каталог видео пуст. Вход скрыт,
   *    пока в разделе нет реальных роликов.
   */
  readonly disabled?: boolean;
}

const ROWS: readonly ScenarioRow[] = [
  { icon: 'spark', label: 'Запустить JIT-онбординг', go: 'jit' },
  // aidepo-45: walkthrough размещает оборудование в случайной property — вход скрыт.
  { icon: 'home', label: 'Проход по дому', go: 'walkthrough', disabled: true },
  // aidepo-51: каталог обучающих роликов пуст — вход скрыт.
  { icon: 'play', label: 'Обучающие ролики', go: 'videos', disabled: true },
  { icon: 'bell', label: 'Уведомления', go: 'notifs' },
  { icon: 'leaf', label: 'Сезонный чек-лист', go: 'season' },
  // «Настройки голосового ввода» переехали в inline-секцию «Распознавание речи»
  // ниже (aidepo-64) — отдельной строки-перехода больше нет.
  { icon: 'info', label: 'О приложении' },
];

// aidepo-64: варианты движка распознавания для переключателя в настройках.
interface EngineOption {
  readonly value: VoiceEnginePref;
  readonly label: string;
}
const ENGINE_OPTIONS: readonly EngineOption[] = [
  { value: 'auto', label: 'Авто' },
  { value: 'device', label: 'На устройстве' },
  { value: 'server', label: 'На сервере' },
];

// Видимые сценарии — отфильтрованы от временно загейченных точек входа
// (aidepo-45 walkthrough, aidepo-51 videos). См. флаг `disabled` выше.
const VISIBLE_ROWS: readonly ScenarioRow[] = ROWS.filter((r) => r.disabled !== true);

interface Stats {
  readonly properties: number;
  readonly items: number;
  readonly equipment: number;
}

type Status = 'loading' | 'ready' | 'error';

function describeError(err: unknown): string {
  return humanizeError(err, 'load');
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} ${one}`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${n} ${few}`;
  return `${n} ${many}`;
}

const PUSH_MESSAGE: Record<PushSetupResult, string> = {
  enabled: 'Push-уведомления включены',
  unsupported: 'Браузер не поддерживает push',
  denied: 'Разрешение на уведомления отклонено',
  'server-disabled': 'Push на сервере не настроен',
  error: 'Не удалось включить push',
};

export function ProfileScreen({ router }: ProfileScreenProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushResult, setPushResult] = useState<PushSetupResult | null>(null);
  const [login, setLogin] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  // aidepo-64: выбранный движок распознавания речи (персист в localStorage).
  const [enginePref, setEnginePref] = useState<VoiceEnginePref>(() => getVoiceEnginePref());
  const nativeSupported = isNativeSttSupported();

  const onPickEngine = (pref: VoiceEnginePref): void => {
    setEnginePref(pref);
    setVoiceEnginePref(pref);
  };

  const onLogout = (): void => {
    setLoggingOut(true);
    // После logout перезагружаем — AuthGate увидит 401 и покажет экран входа.
    void auth.logout().finally(() => window.location.reload());
  };

  const onEnablePush = (): void => {
    setPushBusy(true);
    void enablePushNotifications()
      .then((res) => setPushResult(res))
      .finally(() => setPushBusy(false));
  };

  useEffect(() => {
    let cancelled = false;
    auth
      .me()
      .then((m) => {
        if (!cancelled) setLogin(m.login);
      })
      .catch(() => {
        /* AuthGate уже подтвердил вход — гонку игнорируем */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    properties
      .getPropertyTree()
      .then((tree) => {
        if (cancelled) return;
        let items = 0;
        let equipment = 0;
        for (const prop of tree.properties) {
          items += prop.items.length;
          equipment += prop.equipment.length;
          for (const room of prop.rooms) {
            items += room.items.length;
            equipment += room.equipment.length;
          }
        }
        setStats({ properties: tree.properties.length, items, equipment });
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(describeError(err));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const subtitle =
    stats !== null
      ? `${plural(stats.properties, 'объект', 'объекта', 'объектов')} · с заботой о доме`
      : 'Личный кабинет';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader title="Профиль" subtitle={subtitle} />
      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 24px' }}>
        <div
          style={{
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 16,
            padding: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <Avatar kind="you" name="Я" size={56} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.ink }}>Мой дом</div>
            <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2 }}>
              {status === 'loading' && 'Считаю…'}
              {status === 'error' && (error ?? 'Ошибка загрузки')}
              {status === 'ready' && stats !== null && (
                <>
                  {plural(stats.items, 'вещь', 'вещи', 'вещей')} ·{' '}
                  {plural(stats.equipment, 'единица', 'единицы', 'единиц')} оборудования
                </>
              )}
            </div>
            {/* Версия вшита в бандл при сборке — видно, какой бандл загружен
                (диагностика устаревшего кеша PWA). */}
            <div style={{ fontSize: 11, color: T.ink3, marginTop: 4 }}>
              Версия {__APP_VERSION__}
              {login !== null && <> · вход: {login}</>}
            </div>
          </div>
        </div>

        {isPushSupported() && (
          <div
            style={{
              marginTop: 12,
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: 14,
              padding: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <Icon name="bell" size={18} color={T.ink2} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>Push-уведомления</div>
              <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>
                {pushResult !== null ? PUSH_MESSAGE[pushResult] : 'Напоминания о ТО на устройство'}
              </div>
            </div>
            {pushResult !== 'enabled' && (
              <Btn kind="soft" size="sm" disabled={pushBusy} onClick={onEnablePush}>
                {pushBusy ? '…' : 'Включить'}
              </Btn>
            )}
          </div>
        )}

        <SectionLabel style={{ padding: '14px 4px 6px' }}>Распознавание речи</SectionLabel>
        <div
          style={{
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 14,
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Icon name="mic" size={18} color={T.ink2} />
            <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: T.ink }}>
              Чем распознавать голос
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 4,
              padding: 4,
              background: T.bgAlt,
              borderRadius: 999,
            }}
          >
            {ENGINE_OPTIONS.map((opt) => {
              const active = enginePref === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onPickEngine(opt.value)}
                  aria-pressed={active}
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    borderRadius: 999,
                    background: active ? T.surface : 'transparent',
                    color: active ? T.ink : T.ink3,
                    border: 'none',
                    boxShadow: active ? T.shadow1 : 'none',
                    fontSize: 12.5,
                    fontWeight: 650,
                    fontFamily: T.font,
                    cursor: 'pointer',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 12, color: T.ink3, lineHeight: 1.45 }}>
            {enginePref === 'auto' &&
              'На устройстве, где поддерживается, иначе на сервере. Рекомендуется.'}
            {enginePref === 'device' &&
              (nativeSupported
                ? 'Распознаёт само устройство — быстрее, аудио не загружается на сервер.'
                : 'На этом устройстве недоступно (например, установленное приложение на iOS) — будет использован сервер.')}
            {enginePref === 'server' &&
              'Аудио распознаёт наш сервер (whisper). Работает везде, в том числе офлайн-микрофон без облака устройства.'}
          </div>
        </div>

        <SectionLabel style={{ padding: '14px 4px 6px' }}>Сценарии</SectionLabel>
        <div
          style={{
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 14,
            overflow: 'hidden',
          }}
        >
          {VISIBLE_ROWS.map((r, i) => (
            <div key={r.label}>
              {i > 0 && <div style={{ height: 1, background: T.divider, marginLeft: 52 }} />}
              <div
                onClick={r.go !== undefined ? () => router.go(r.go as TabId | ScreenId) : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 14,
                  cursor: r.go !== undefined ? 'pointer' : 'default',
                }}
              >
                <Icon name={r.icon} size={18} color={T.ink2} />
                <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: T.ink }}>{r.label}</div>
                {r.go !== undefined && <Icon name="chevR" size={16} color={T.ink3} />}
              </div>
            </div>
          ))}
        </div>

        <Btn
          kind="ghost"
          onClick={onLogout}
          disabled={loggingOut}
          style={{ width: '100%', marginTop: 16 }}
        >
          {loggingOut ? 'Выхожу…' : 'Выйти'}
        </Btn>
      </div>
    </div>
  );
}
